import { type ReactNode, useMemo } from "react";
import { Descriptions as AntdDescriptions, Flex, Typography } from "antd";
import { createStyles } from "antd-style";

import type { IEntityPermissions } from "@av/config/api-config/permissions/entity-permissions.interface";
import { LineLoader } from "../loaders/LineLoader";

import type { AvE2EProps } from "../../types";
import type { DescriptionsProps as AntdDescriptionsProps } from "antd/es/descriptions";
import type { DescriptionsItemProps as AntdDescriptionsItemProps } from "antd/es/descriptions/Item";

export type DescriptionsItemProps = AvE2EProps &
  Omit<AntdDescriptionsItemProps, "key"> & {
    label: ReactNode;
    description?: ReactNode;
    /**
     * Maps this description item to one or more permission fields.
     * If multiple fields are provided, the item is allowed when at least one field passes the permission check.
     * If no permissions are provided, the item is shown by default.
     */
    permissionTargetField?: string | string[];
  };

export type DescriptionsProps = AvE2EProps &
  Omit<AntdDescriptionsProps, "items"> & {
    items: DescriptionsItemProps[];
    /**
     * The permission object used to decide whether to show a field
     * Permission resolution falls back to show the field(s) if permission object is not provided
     */
    permissions?: IEntityPermissions;
    isDataLoading?: boolean;
    /**
     * CSS width of the label column, e.g. "50%" or "120px". Defaults to 30% so the value gets the remaining 70%.
     * Item-level `styles.label` and component-level `styles.label` still take precedence.
     */
    labelWidth?: string;
  };

const DEFAULT_LABEL_WIDTH = "30%";

const useStyles = createStyles(({ css, token }, { labelWidth }: { labelWidth: string }) => ({
  descriptions: css`
    /* :where keeps specificity low so consumer CSS (e.g. mobile stacking) can override the width */
    & :where(.ant-descriptions-item-label) {
      width: ${labelWidth};
    }

    /* Bordered items have no colon spacer, so guarantee a gap between label and value */
    &.ant-descriptions-bordered
      > .ant-descriptions-view
      .ant-descriptions-row
      > .ant-descriptions-item-label {
      padding-inline-end: ${token.paddingXXS}px;
    }
  `,
  label: css`
    color: ${token.colorTextBase};
  `,
  description: css`
    font-size: ${token.fontSizeSM}px;
    color: ${token.colorTextSecondary};
  `,
}));

export function Descriptions(props: DescriptionsProps) {
  const {
    testIdPrefix,
    items = [],
    permissions,
    isDataLoading: isPermissionsLoading,
    labelWidth = DEFAULT_LABEL_WIDTH,

    ...restProps
  } = props;
  const { styles, cx } = useStyles({ labelWidth });
  const mappedItems = useMemo(() => {
    const visibleItems = [];

    for (const item of items) {
      const { permissionTargetField: field, description, ...rest } = item;
      const fields = Array.isArray(field) ? field : [field];

      if (isPermissionsLoading) {
        visibleItems.push({
          key: item.testIdPrefix,
          label: <LineLoader />,
          children: <LineLoader />,
        });
      } else if (
        !permissions ||
        !field ||
        fields.some((f) => f && permissions.isFieldViewable(f))
      ) {
        const labelNode =
          typeof item.label === "string" ? (
            <Typography.Text data-testid={`${item.testIdPrefix}-label`} className={styles.label}>
              {item.label}
            </Typography.Text>
          ) : (
            item.label
          );

        const descriptionNode =
          description == null ? null : typeof description === "string" ? (
            <Typography.Text
              data-testid={`${item.testIdPrefix}-description`}
              className={styles.description}
            >
              {description}
            </Typography.Text>
          ) : (
            description
          );

        visibleItems.push({
          ...rest,
          key: item.testIdPrefix,
          label: descriptionNode ? (
            <Flex vertical>
              {labelNode}
              {descriptionNode}
            </Flex>
          ) : (
            labelNode
          ),
        });
      }
    }

    return visibleItems;
  }, [isPermissionsLoading, items, permissions, styles.label, styles.description]);

  return (
    <AntdDescriptions
      data-testid={`${testIdPrefix}-descriptions`}
      {...restProps}
      className={cx(styles.descriptions, restProps.className)}
      items={mappedItems}
    />
  );
}
